import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.scss';
import { Message } from './types';
import { useTheme } from '@root/context/ThemeContext';
import agent from '@root/agent';
import { toast } from 'sonner';
import { useMessageProgress } from '@root/hooks/useMessageProgress';
import { CapitalizeFirstLetter } from '../utilities/CapitalizeFirstLetter';
import ReactMarkdown from 'react-markdown';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import ViperButton from '@root/pages/workloload-scanning/ViperButton';

// Icon shortcode plugin for ReactMarkdown (same as Dashboard)
const iconShortcodePlugin = () => {
  const replacements = {
    ':check:': '',
    ':info:': 'ℹ️',
    ':warning:': '⚠️',
    ':star:': '⭐',
    ':rocket:': '🚀',
    ':x:': '❌',
    ':bulb:': '💡',
    ':lock:': '🔒',
    ':shield:': '🛡️',
    ':fire:': '🔥',
    ':bug:': '🐞',
    ':bell:': '🔔',
    ':hourglass:': '⏳',
    ':zap:': '⚡',
    ':question:': '❓',
    ':arrow:': '➡️',
    ':up:': '⬆️',
    ':down:': '⬇️',
    ':left:': '⬅️',
    ':right:': '➡️',
  };
  return {
    // Replace text nodes
    text: node => {
      let text = node.value;
      Object.entries(replacements).forEach(([key, val]) => {
        text = text.split(key).join(val);
      });
      return text;
    },
  };
};

// Copy icon component (same as Dashboard)
const CopyIcon = ({ onClick, copied }) => (
  <span className="copy-icon" onClick={onClick} title={copied ? 'Copied!' : 'Copy'}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
    {copied && <span style={{ marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>Copied!</span>}
  </span>
);

// Message content component with actions (similar to Dashboard)
const MessageContent = ({ message, onFeedbackUpdate }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Silent fail - no toast notification
    }
  };

  const handleFeedback = (type: 'up' | 'down') => {
    const newFeedback = message.feedback === type ? null : type;
    onFeedbackUpdate(message.id, newFeedback);
  };

  return (
    <div className="message-content">
      <div className="message-text">
        {message.isUser ? (
          message.text
        ) : (
          <div className="markdown-content markdown_viper">
            <ReactMarkdown components={iconShortcodePlugin()}>
              {message.text}
            </ReactMarkdown>
          </div>
        )}
      </div>
      
      {/* Action buttons for AI messages (not for welcome message) */}
      {!message.isUser && !message.text.includes('Hi') && !message.text.includes('I am Viper AI') && (
        <div className="message-actions">
          <div className="actions-group">
            <button className="action-button" onClick={handleCopy}>
              <CopyIcon onClick={handleCopy} copied={copied} />
            </button>
            <button
              className={`action-button ${message.feedback === 'up' ? 'active' : ''}`}
              onClick={() => handleFeedback('up')}
            >
              <ThumbsUp size={14} />
            </button>
            <button
              className={`action-button ${message.feedback === 'down' ? 'active' : ''}`}
              onClick={() => handleFeedback('down')}
            >
              <ThumbsDown size={14} />
            </button>
          </div>
        </div>
      )}
      
      <div className="message-time">
        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

interface ChatBotProps {
  initialMessage?: string;
  onOpenChange?: (isOpen: boolean) => void;
}

const DRAG_THRESHOLD = 5; // pixels moved before considering it a drag

const ChatBot: React.FC<ChatBotProps> = ({ initialMessage, onOpenChange }) => {
  const { isDarkMode } = useTheme();
  // const query = useQuery(); // Removed unused import
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pollingMessageId, setPollingMessageId] = useState<string | null>(null);
  const [pollingConversationId, setPollingConversationId] = useState<string | null>(null);
  // const [currentFollowUps, setCurrentFollowUps] = useState<string[]>([]); // Removed unused state
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Draggable button state
  const btnRef = useRef<HTMLButtonElement>(null);
  const dragState = useRef({
    isDragging: false,
    wasDragged: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    currentX: 0,
    currentY: 0,
  });

  const handlePointerDown = (e: React.PointerEvent) => {
    const btn = btnRef.current;
    if (!btn) return;
    btn.setPointerCapture(e.pointerId);
    const rect = btn.getBoundingClientRect();
    dragState.current = {
      ...dragState.current,
      isDragging: true,
      wasDragged: false,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const ds = dragState.current;
    if (!ds.isDragging) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    if (!ds.wasDragged && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    ds.wasDragged = true;

    const btn = btnRef.current;
    if (!btn) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const bw = btn.offsetWidth;
    const bh = btn.offsetHeight;

    // Compute new position clamped to viewport
    let newLeft = e.clientX - ds.offsetX;
    let newTop = e.clientY - ds.offsetY;
    newLeft = Math.max(0, Math.min(newLeft, vw - bw));
    newTop = Math.max(0, Math.min(newTop, vh - bh));

    // Use transform for GPU-accelerated movement (no layout reflow)
    btn.style.right = 'auto';
    btn.style.bottom = 'auto';
    btn.style.left = '0px';
    btn.style.top = '0px';
    btn.style.transform = `translate3d(${newLeft}px, ${newTop}px, 0)`;

    ds.currentX = newLeft;
    ds.currentY = newTop;
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const ds = dragState.current;
    const wasDragged = ds.wasDragged;
    ds.isDragging = false;

    const btn = btnRef.current;
    if (btn) {
      btn.releasePointerCapture(e.pointerId);

      // Snap to nearest horizontal edge for a polished feel
      if (wasDragged) {
        const vw = window.innerWidth;
        const bw = btn.offsetWidth;
        const midpoint = vw / 2;
        const margin = 24;
        const snapLeft = ds.currentX < midpoint - bw / 2;
        const finalX = snapLeft ? margin : vw - bw - margin;
        const finalY = ds.currentY;

        btn.style.transition = 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)';
        btn.style.transform = `translate3d(${finalX}px, ${finalY}px, 0)`;

        ds.currentX = finalX;
        ds.currentY = finalY;

        const clearTransition = () => {
          btn.style.transition = '';
          btn.removeEventListener('transitionend', clearTransition);
        };
        btn.addEventListener('transitionend', clearTransition);
      }
    }

    // If it was just a click (not a drag), toggle the chat
    if (!wasDragged) {
      toggleChat();
    }
  };

  const { progress, followUpSuggestions } = useMessageProgress(pollingConversationId, pollingMessageId, isTyping);

  // Handle initial message
  useEffect(() => {
    if (initialMessage && !hasUserTyped) {
      setInputValue(initialMessage);
      // Auto-open the chat when initial message is provided
      if (!isOpen) {
        setIsOpen(true);
        document.body.classList.add('chat-open');
      }
    }
  }, [initialMessage, hasUserTyped, isOpen]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Stop thinking when progress is complete and fetch final response
  useEffect(() => {
    if (progress && (progress as any)?.status === 'complete' && pollingConversationId) {
      setIsTyping(false);
      setIsGenerating(false);

      // Set follow-ups from progress (currently not used in UI)
      // if (followUpSuggestions && followUpSuggestions.length > 0) {
      //   setCurrentFollowUps(followUpSuggestions);
      // }

      // Fetch the latest conversation to get the assistant response
      const fetchFinalResponse = async () => {
        try {
          const response = await agent.ViperAI.get_viper_ai_chat_history(pollingConversationId);
          if (response && response.messages && Array.isArray(response.messages)) {
            // Find the latest assistant message
            const assistantMessages = response.messages.filter(
              msg => msg.role === 'assistant' || msg.message?.role === 'assistant',
            );

            if (assistantMessages.length > 0) {
              const latestAssistant = assistantMessages[assistantMessages.length - 1];
              const messageContent = latestAssistant.content || latestAssistant.message?.content || latestAssistant.text;

              // Add the assistant message if not already present
              setMessages(prevMessages => {
                const lastMessage = prevMessages[prevMessages.length - 1];
                if (lastMessage?.isUser !== false) {
                  return [...prevMessages, {
                    id: Date.now().toString(),
                    text: messageContent || 'Analysis complete! Your security analysis has been processed successfully.',
                    isUser: false,
                    timestamp: new Date()
                  }];
                }
                return prevMessages;
              });
            }
          }
        } catch (error) {
          console.error('Error fetching final response:', error);
          // Add fallback message
          setMessages(prevMessages => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage?.isUser !== false) {
              return [
                ...prevMessages,
                {
                  id: Date.now().toString(),
                  text: 'Analysis complete! Your security analysis has been processed successfully.',
                  isUser: false,
                  timestamp: new Date()
                },
              ];
            }
            return prevMessages;
          });
        } finally {
          setPollingMessageId(null);
          setPollingConversationId(null);
        }
      };

      fetchFinalResponse();
    }
  }, [progress, followUpSuggestions, pollingConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Cleanup body class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('chat-open');
    };
  }, []);

  // Adjust textarea height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);


  // Add greeting message when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const userInfo = JSON.parse(localStorage.getItem('User') || '{}');
      setMessages([{
        id: '1',
        text: `Hi ${CapitalizeFirstLetter(userInfo?.user?.name)}! I am Viper AI, your security analyst. How can I help you today?`,
        isUser: false,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim()) return;

    // Get conversation ID based on messages or fetch a new one
    let conversationId = messages?.find(item => !item.isUser)?.conversationId;
    console.log('Current conversationId from messages:', conversationId);

    // If no conversationId exists, fetch a new one
    if (!conversationId) {
      try {
        const response = await agent.ViperAI.get_conversation_id({});
        console.log('API response for conversation ID:', response);
        conversationId = response?.conversation?.id || response;
        console.log('Extracted conversationId:', conversationId);
        
        // Validate that we got a valid conversation ID
        if (!conversationId) {
          console.error('No conversation ID returned from API');
          toast.error('Failed to start conversation. Please try again.');
          return;
        }
      } catch (error) {
        console.error('Error fetching conversation ID:', error);
        toast.error('Failed to start conversation. Please try again.');
        return;
      }
    }

    // Validate conversation ID before proceeding
    if (!conversationId || conversationId === 'undefined') {
      console.error('Invalid conversation ID:', conversationId);
      toast.error('Invalid conversation. Please try again.');
      return;
    }

    // Add user message
    const newMessage: Message = {
      id: Date.now().toString(),
      text: text,
      isUser: true,
      timestamp: new Date(),
      conversationId: conversationId
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setHasUserTyped(true);
    setIsTyping(true);
    setIsGenerating(true);

    // Start progress polling immediately
    const targetConversationId = (conversationId as any)?.conversation?.id || conversationId;
    console.log('Final targetConversationId:', targetConversationId);
    
    // Final validation before API call
    if (!targetConversationId || targetConversationId === 'undefined') {
      console.error('Invalid target conversation ID:', targetConversationId);
      toast.error('Invalid conversation ID. Please try again.');
      setIsTyping(false);
      setIsGenerating(false);
      return;
    }

    setPollingConversationId(targetConversationId);

    // Generate a message ID for polling (UUID v4)
    const messageId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

    // Start polling immediately with our generated ID
    setPollingMessageId(messageId);

    // Fire and forget the message request - don't wait for completion
    agent.ViperAI.get_viper_ai_chat_data({ content: text, message_id: messageId }, targetConversationId)
      .then(response => {
        if (response?.detail) {
          toast.warning('Message limit reached');
          setIsTyping(false);
          setIsGenerating(false);
          setPollingMessageId(null);
          setPollingConversationId(null);
          return;
        }

        console.log('Message sent successfully, polling will handle the response');
      })
      .catch(error => {
        console.error('Error sending message:', error);
        // Only show error if progress hasn't completed yet
        if (isTyping) {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              id: (Date.now() + 1).toString(),
              text: 'Sorry, I encountered an error processing your request. Please try again.',
              isUser: false,
              timestamp: new Date()
            },
          ]);
          setIsTyping(false);
          setIsGenerating(false);
          setPollingMessageId(null);
          setPollingConversationId(null);
        }
      });
  };


  const handleStopGenerating = () => {
    setIsGenerating(false);
    setIsTyping(false);
  };

  const handleFeedbackUpdate = (messageId: string, feedback: 'up' | 'down' | null) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, feedback } : msg
      )
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const scrollHeight = inputRef.current.scrollHeight;
      const maxHeight = 120; // max-height from CSS
      const minHeight = 20; // min-height from CSS
      
      if (scrollHeight > maxHeight) {
        inputRef.current.style.height = `${maxHeight}px`;
        inputRef.current.style.overflowY = 'auto';
      } else {
        inputRef.current.style.height = `${Math.max(scrollHeight, minHeight)}px`;
        inputRef.current.style.overflowY = 'hidden';
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    adjustTextareaHeight();
  };

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // Add/remove body class to shift main content
    if (newIsOpen) {
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }
    
    // Notify parent component of open state change
    if (onOpenChange) {
      onOpenChange(newIsOpen);
    }
  };


  return (
    <>
      {/* Floating Button - Only show when chat is closed */}
      {!isOpen && (
        <button
          ref={btnRef}
          className="chatbot-floating-btn"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          aria-label="Open AI Assistant"
          style={{ touchAction: 'none', willChange: 'transform' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 17 23" fill="none">
            <path
              d="M9.43085 8.84685C9.58667 8.3794 10.2468 8.3794 10.4026 8.84685L11.3162 11.5906C11.5173 12.1938 11.8562 12.7418 12.3059 13.1913C12.7556 13.6407 13.3038 13.9792 13.907 14.18L16.6494 15.0937C17.1169 15.2495 17.1169 15.9096 16.6494 16.0654L13.9056 16.9791C13.3025 17.1802 12.7544 17.519 12.305 17.9687C11.8555 18.4184 11.517 18.9666 11.3162 19.5699L10.4026 22.3122C10.369 22.4146 10.3039 22.5037 10.2167 22.5669C10.1294 22.6301 10.0244 22.6641 9.91672 22.6641C9.80899 22.6641 9.70401 22.6301 9.61676 22.5669C9.52951 22.5037 9.46445 22.4146 9.43085 22.3122L8.5172 19.5684C8.31624 18.9654 7.97762 18.4175 7.52818 17.9681C7.07874 17.5186 6.53082 17.18 5.92781 16.9791L3.18402 16.0654C3.08166 16.0318 2.99253 15.9667 2.92934 15.8795C2.86614 15.7922 2.83212 15.6873 2.83212 15.5795C2.83212 15.4718 2.86614 15.3668 2.92934 15.2796C2.99253 15.1923 3.08166 15.1273 3.18402 15.0937L5.92781 14.18C6.53082 13.9791 7.07874 13.6404 7.52818 13.191C7.97762 12.7416 8.31624 12.1936 8.5172 11.5906L9.43085 8.84685ZM3.95886 1.62404C3.97917 1.56269 4.0183 1.50931 4.07068 1.47148C4.12307 1.43364 4.18604 1.41328 4.25066 1.41328C4.31528 1.41328 4.37825 1.43364 4.43064 1.47148C4.48302 1.50931 4.52215 1.56269 4.54246 1.62404L5.09065 3.27003C5.33571 4.00378 5.91082 4.57889 6.64457 4.82394L8.29056 5.37214C8.3519 5.39245 8.40528 5.43158 8.44312 5.48396C8.48095 5.53634 8.50132 5.59932 8.50132 5.66394C8.50132 5.72856 8.48095 5.79153 8.44312 5.84391C8.40528 5.8963 8.3519 5.93543 8.29056 5.95574L6.64457 6.50393C6.28247 6.62411 5.95344 6.82716 5.68366 7.09694C5.41389 7.36671 5.21083 7.69575 5.09065 8.05785L4.54246 9.70384C4.52215 9.76518 4.48302 9.81856 4.43064 9.8564C4.37825 9.89423 4.31528 9.9146 4.25066 9.9146C4.18604 9.9146 4.12307 9.89423 4.07068 9.8564C4.0183 9.81856 3.97917 9.76518 3.95886 9.70384L3.41067 8.05785C3.29049 7.69575 3.08743 7.36671 2.81766 7.09694C2.54788 6.82716 2.21885 6.62411 1.85675 6.50393L0.210759 5.95574C0.149416 5.93543 0.0960346 5.8963 0.0581995 5.84391C0.0203643 5.79153 0 5.72856 0 5.66394C0 5.59932 0.0203643 5.53634 0.0581995 5.48396C0.0960346 5.43158 0.149416 5.39245 0.210759 5.37214L1.85675 4.82394C2.21885 4.70377 2.54788 4.50071 2.81766 4.23094C3.08743 3.96116 3.29049 3.63212 3.41067 3.27003L3.95886 1.62404ZM13.9722 0.138114C13.9862 0.0977805 14.0124 0.0628062 14.0472 0.0380536C14.0819 0.0133009 14.1236 0 14.1663 0C14.209 0 14.2506 0.0133009 14.2854 0.0380536C14.3201 0.0628062 14.3463 0.0977805 14.3603 0.138114L14.7258 1.2345C14.8887 1.72461 15.2726 2.10849 15.7627 2.27138L16.8591 2.63685C16.8994 2.65083 16.9344 2.67703 16.9591 2.71181C16.9839 2.74659 16.9972 2.78822 16.9972 2.83091C16.9972 2.8736 16.9839 2.91522 16.9591 2.95C16.9344 2.98478 16.8994 3.01099 16.8591 3.02497L15.7627 3.39043C15.5214 3.47117 15.3021 3.60686 15.1221 3.7868C14.9422 3.96673 14.8065 4.186 14.7258 4.42732L14.3603 5.5237C14.3463 5.56404 14.3201 5.59901 14.2854 5.62376C14.2506 5.64852 14.209 5.66182 14.1663 5.66182C14.1236 5.66182 14.0819 5.64852 14.0472 5.62376C14.0124 5.59901 13.9862 5.56404 13.9722 5.5237L13.6067 4.42732C13.526 4.186 13.3903 3.96673 13.2104 3.7868C13.0304 3.60686 12.8112 3.47117 12.5699 3.39043L11.4749 3.02497C11.4346 3.01099 11.3996 2.98478 11.3748 2.95C11.3501 2.91522 11.3368 2.8736 11.3368 2.83091C11.3368 2.78822 11.3501 2.74659 11.3748 2.71181C11.3996 2.67703 11.4346 2.65083 11.4749 2.63685L12.5713 2.27138C13.0614 2.10849 13.4453 1.72461 13.6082 1.2345L13.9722 0.138114Z"
              fill="#00DBDE"
            />
          </svg>
        </button>
      )}

      {/* Chat Side Panel */}
      <div className={`chatbot-panel ${isOpen ? 'open' : ''} ${isDarkMode ? 'dark' : 'light'}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <div className="chatbot-icon">
              <ViperButton onClick={() => {}} iconOnly={true}/>
            </div>
            {/* <span className="chatbot-title">Viper AI</span> */}
          </div>
          <div className="chatbot-header-right">
            <button className="chatbot-close-btn" onClick={toggleChat}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>


        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.isUser ? 'user' : 'ai'}`}>
              {!message.isUser && (
                <div className="message-avatar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 17 23" fill="none">
                    <path
                      d="M9.43085 8.84685C9.58667 8.3794 10.2468 8.3794 10.4026 8.84685L11.3162 11.5906C11.5173 12.1938 11.8562 12.7418 12.3059 13.1913C12.7556 13.6407 13.3038 13.9792 13.907 14.18L16.6494 15.0937C17.1169 15.2495 17.1169 15.9096 16.6494 16.0654L13.9056 16.9791C13.3025 17.1802 12.7544 17.519 12.305 17.9687C11.8555 18.4184 11.517 18.9666 11.3162 19.5699L10.4026 22.3122C10.369 22.4146 10.3039 22.5037 10.2167 22.5669C10.1294 22.6301 10.0244 22.6641 9.91672 22.6641C9.80899 22.6641 9.70401 22.6301 9.61676 22.5669C9.52951 22.5037 9.46445 22.4146 9.43085 22.3122L8.5172 19.5684C8.31624 18.9654 7.97762 18.4175 7.52818 17.9681C7.07874 17.5186 6.53082 17.18 5.92781 16.9791L3.18402 16.0654C3.08166 16.0318 2.99253 15.9667 2.92934 15.8795C2.86614 15.7922 2.83212 15.6873 2.83212 15.5795C2.83212 15.4718 2.86614 15.3668 2.92934 15.2796C2.99253 15.1923 3.08166 15.1273 3.18402 15.0937L5.92781 14.18C6.53082 13.9791 7.07874 13.6404 7.52818 13.191C7.97762 12.7416 8.31624 12.1936 8.5172 11.5906L9.43085 8.84685ZM3.95886 1.62404C3.97917 1.56269 4.0183 1.50931 4.07068 1.47148C4.12307 1.43364 4.18604 1.41328 4.25066 1.41328C4.31528 1.41328 4.37825 1.43364 4.43064 1.47148C4.48302 1.50931 4.52215 1.56269 4.54246 1.62404L5.09065 3.27003C5.33571 4.00378 5.91082 4.57889 6.64457 4.82394L8.29056 5.37214C8.3519 5.39245 8.40528 5.43158 8.44312 5.48396C8.48095 5.53634 8.50132 5.59932 8.50132 5.66394C8.50132 5.72856 8.48095 5.79153 8.44312 5.84391C8.40528 5.8963 8.3519 5.93543 8.29056 5.95574L6.64457 6.50393C6.28247 6.62411 5.95344 6.82716 5.68366 7.09694C5.41389 7.36671 5.21083 7.69575 5.09065 8.05785L4.54246 9.70384C4.52215 9.76518 4.48302 9.81856 4.43064 9.8564C4.37825 9.89423 4.31528 9.9146 4.25066 9.9146C4.18604 9.9146 4.12307 9.89423 4.07068 9.8564C4.0183 9.81856 3.97917 9.76518 3.95886 9.70384L3.41067 8.05785C3.29049 7.69575 3.08743 7.36671 2.81766 7.09694C2.54788 6.82716 2.21885 6.62411 1.85675 6.50393L0.210759 5.95574C0.149416 5.93543 0.0960346 5.8963 0.0581995 5.84391C0.0203643 5.79153 0 5.72856 0 5.66394C0 5.59932 0.0203643 5.53634 0.0581995 5.48396C0.0960346 5.43158 0.149416 5.39245 0.210759 5.37214L1.85675 4.82394C2.21885 4.70377 2.54788 4.50071 2.81766 4.23094C3.08743 3.96116 3.29049 3.63212 3.41067 3.27003L3.95886 1.62404ZM13.9722 0.138114C13.9862 0.0977805 14.0124 0.0628062 14.0472 0.0380536C14.0819 0.0133009 14.1236 0 14.1663 0C14.209 0 14.2506 0.0133009 14.2854 0.0380536C14.3201 0.0628062 14.3463 0.0977805 14.3603 0.138114L14.7258 1.2345C14.8887 1.72461 15.2726 2.10849 15.7627 2.27138L16.8591 2.63685C16.8994 2.65083 16.9344 2.67703 16.9591 2.71181C16.9839 2.74659 16.9972 2.78822 16.9972 2.83091C16.9972 2.8736 16.9839 2.91522 16.9591 2.95C16.9344 2.98478 16.8994 3.01099 16.8591 3.02497L15.7627 3.39043C15.5214 3.47117 15.3021 3.60686 15.1221 3.7868C14.9422 3.96673 14.8065 4.186 14.7258 4.42732L14.3603 5.5237C14.3463 5.56404 14.3201 5.59901 14.2854 5.62376C14.2506 5.64852 14.209 5.66182 14.1663 5.66182C14.1236 5.66182 14.0819 5.64852 14.0472 5.62376C14.0124 5.59901 13.9862 5.56404 13.9722 5.5237L13.6067 4.42732C13.526 4.186 13.3903 3.96673 13.2104 3.7868C13.0304 3.60686 12.8112 3.47117 12.5699 3.39043L11.4749 3.02497C11.4346 3.01099 11.3996 2.98478 11.3748 2.95C11.3501 2.91522 11.3368 2.8736 11.3368 2.83091C11.3368 2.78822 11.3501 2.74659 11.3748 2.71181C11.3996 2.67703 11.4346 2.65083 11.4749 2.63685L12.5713 2.27138C13.0614 2.10849 13.4453 1.72461 13.6082 1.2345L13.9722 0.138114Z"
                      fill="#00DBDE"
                    />
                  </svg>
                </div>
              )}
              <MessageContent message={message} onFeedbackUpdate={handleFeedbackUpdate} />
            </div>
          ))}
          {isTyping && (
            <div className="message ai">
              <div className="message-avatar">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 17 23" fill="none">
                  <path
                    d="M9.43085 8.84685C9.58667 8.3794 10.2468 8.3794 10.4026 8.84685L11.3162 11.5906C11.5173 12.1938 11.8562 12.7418 12.3059 13.1913C12.7556 13.6407 13.3038 13.9792 13.907 14.18L16.6494 15.0937C17.1169 15.2495 17.1169 15.9096 16.6494 16.0654L13.9056 16.9791C13.3025 17.1802 12.7544 17.519 12.305 17.9687C11.8555 18.4184 11.517 18.9666 11.3162 19.5699L10.4026 22.3122C10.369 22.4146 10.3039 22.5037 10.2167 22.5669C10.1294 22.6301 10.0244 22.6641 9.91672 22.6641C9.80899 22.6641 9.70401 22.6301 9.61676 22.5669C9.52951 22.5037 9.46445 22.4146 9.43085 22.3122L8.5172 19.5684C8.31624 18.9654 7.97762 18.4175 7.52818 17.9681C7.07874 17.5186 6.53082 17.18 5.92781 16.9791L3.18402 16.0654C3.08166 16.0318 2.99253 15.9667 2.92934 15.8795C2.86614 15.7922 2.83212 15.6873 2.83212 15.5795C2.83212 15.4718 2.86614 15.3668 2.92934 15.2796C2.99253 15.1923 3.08166 15.1273 3.18402 15.0937L5.92781 14.18C6.53082 13.9791 7.07874 13.6404 7.52818 13.191C7.97762 12.7416 8.31624 12.1936 8.5172 11.5906L9.43085 8.84685ZM3.95886 1.62404C3.97917 1.56269 4.0183 1.50931 4.07068 1.47148C4.12307 1.43364 4.18604 1.41328 4.25066 1.41328C4.31528 1.41328 4.37825 1.43364 4.43064 1.47148C4.48302 1.50931 4.52215 1.56269 4.54246 1.62404L5.09065 3.27003C5.33571 4.00378 5.91082 4.57889 6.64457 4.82394L8.29056 5.37214C8.3519 5.39245 8.40528 5.43158 8.44312 5.48396C8.48095 5.53634 8.50132 5.59932 8.50132 5.66394C8.50132 5.72856 8.48095 5.79153 8.44312 5.84391C8.40528 5.8963 8.3519 5.93543 8.29056 5.95574L6.64457 6.50393C6.28247 6.62411 5.95344 6.82716 5.68366 7.09694C5.41389 7.36671 5.21083 7.69575 5.09065 8.05785L4.54246 9.70384C4.52215 9.76518 4.48302 9.81856 4.43064 9.8564C4.37825 9.89423 4.31528 9.9146 4.25066 9.9146C4.18604 9.9146 4.12307 9.89423 4.07068 9.8564C4.0183 9.81856 3.97917 9.76518 3.95886 9.70384L3.41067 8.05785C3.29049 7.69575 3.08743 7.36671 2.81766 7.09694C2.54788 6.82716 2.21885 6.62411 1.85675 6.50393L0.210759 5.95574C0.149416 5.93543 0.0960346 5.8963 0.0581995 5.84391C0.0203643 5.79153 0 5.72856 0 5.66394C0 5.59932 0.0203643 5.53634 0.0581995 5.48396C0.0960346 5.43158 0.149416 5.39245 0.210759 5.37214L1.85675 4.82394C2.21885 4.70377 2.54788 4.50071 2.81766 4.23094C3.08743 3.96116 3.29049 3.63212 3.41067 3.27003L3.95886 1.62404ZM13.9722 0.138114C13.9862 0.0977805 14.0124 0.0628062 14.0472 0.0380536C14.0819 0.0133009 14.1236 0 14.1663 0C14.209 0 14.2506 0.0133009 14.2854 0.0380536C14.3201 0.0628062 14.3463 0.0977805 14.3603 0.138114L14.7258 1.2345C14.8887 1.72461 15.2726 2.10849 15.7627 2.27138L16.8591 2.63685C16.8994 2.65083 16.9344 2.67703 16.9591 2.71181C16.9839 2.74659 16.9972 2.78822 16.9972 2.83091C16.9972 2.8736 16.9839 2.91522 16.9591 2.95C16.9344 2.98478 16.8994 3.01099 16.8591 3.02497L15.7627 3.39043C15.5214 3.47117 15.3021 3.60686 15.1221 3.7868C14.9422 3.96673 14.8065 4.186 14.7258 4.42732L14.3603 5.5237C14.3463 5.56404 14.3201 5.59901 14.2854 5.62376C14.2506 5.64852 14.209 5.66182 14.1663 5.66182C14.1236 5.66182 14.0819 5.64852 14.0472 5.62376C14.0124 5.59901 13.9862 5.56404 13.9722 5.5237L13.6067 4.42732C13.526 4.186 13.3903 3.96673 13.2104 3.7868C13.0304 3.60686 12.8112 3.47117 12.5699 3.39043L11.4749 3.02497C11.4346 3.01099 11.3996 2.98478 11.3748 2.95C11.3501 2.91522 11.3368 2.8736 11.3368 2.83091C11.3368 2.78822 11.3501 2.74659 11.3748 2.71181C11.3996 2.67703 11.4346 2.65083 11.4749 2.63685L12.5713 2.27138C13.0614 2.10849 13.4453 1.72461 13.6082 1.2345L13.9722 0.138114Z"
                    fill="#00DBDE"
                  />
                </svg>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chatbot-input">
          {isGenerating && (
            <div className="stop-generating-container">
              <button className="stop-generating-btn" onClick={handleStopGenerating}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" fill="currentColor"/>
                </svg>
                Stop Generating
              </button>
            </div>
          )}
          <div className="input-container">
            <textarea
              ref={inputRef}
              placeholder="Ask me anything..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              maxLength={1024}
              rows={1}
            />
            <div className="input-controls">
              <div className="char-counter">{inputValue.length} / 1024</div>
              <button 
                className="send-btn"
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim()}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatBot;
